import { SurveyFormSummary } from "@coaching-culture/types";
import {
  Flex,
  Modal,
  Text,
  Button,
  Circle,
  EmptyState,
  formatDate,
} from "@coaching-culture/ui";
import { fromUnixTime } from "date-fns";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaClipboardCheck, FaQuestion } from "react-icons/fa";
import styled from "styled-components";
import { ItemContainer, ItemsList } from "components/ShareModal/Drawer";
import { Link } from "react-router-dom";

const Container = styled.div`
  border: 1px solid ${(props) => props.theme.colors.grey3};
`;

const FormItem = ({
  onClick,
  form,
}: {
  onClick: () => void;
  form: SurveyFormSummary;
}) => {
  return (
    <ItemContainer onClick={onClick}>
      <Flex width="400px" maxWidth="100%" alignItems="center">
        <Circle icon={FaClipboardCheck} mr={2} size="small" />
        <Text fontWeight={500} fontSize={4}>
          {form.name}
        </Text>
      </Flex>
    </ItemContainer>
  );
};

type SurveyFormSelectorModalProps = {
  onSelect: (form: SurveyFormSummary | null) => void;
  onCancel: () => void;
  forms: SurveyFormSummary[];
};

function SurveyFormSelectorModal({
  onSelect,
  onCancel,
  forms,
}: SurveyFormSelectorModalProps) {
  return (
    <Modal
      width={600}
      minHeight={300}
      p={0}
      onClose={onCancel}
      showCloseButton
      style={{ overflow: "hidden" }}
    >
      <Flex alignItems="center" flexDirection="column" p={3}>
        <Circle color="primary" icon={FaClipboardCheck} size="large" mb={2} />
        <Text fontWeight={600} fontSize={6}>
          Choose Survey Form
        </Text>
      </Flex>
      {forms.length > 0 ? (
        <ItemsList>
          {forms.map((x) => (
            <FormItem form={x} onClick={() => onSelect(x)} />
          ))}
        </ItemsList>
      ) : (
        <EmptyState
          compact
          icon={FaClipboardCheck}
          title="No forms available"
          action={
            <Link to="/success/surveys/forms/create">Create a new Form</Link>
          }
        >
          Create a survey form to send it in a campaign.
        </EmptyState>
      )}
    </Modal>
  );
}

export type SurveyFormSelectorProps = {
  value: SurveyFormSummary | null;
  onChange: (val: SurveyFormSummary) => void;
};

export function SurveyFormSelector({
  value,
  onChange,
}: SurveyFormSelectorProps) {
  const [modal, setModal] = useState<boolean>(false);
  const [forms, setForms] = useState<SurveyFormSummary[] | null>(null);

  useEffect(() => {
    axios.get("/api/survey-forms").then(({ data }) => setForms(data));
  }, []);

  const onSelect = (item: SurveyFormSummary | null) => {
    setModal(false);
    onChange(item);
  };

  return (
    <>
      {modal && (
        <SurveyFormSelectorModal
          onSelect={onSelect}
          forms={forms}
          onCancel={() => setModal(false)}
        />
      )}
      <Container>
        <Flex alignItems="center" justifyContent="space-between" p={3}>
          <Flex alignItems="center">
            <Circle
              size="large"
              icon={value == null ? FaQuestion : FaClipboardCheck}
              color={value == null ? "grey3" : "primary"}
              mr={3}
            />
            <div>
              <Text fontSize={4}>{value?.name ?? "<None Selected>"}</Text>
              <Text color="grey2">
                {value == null
                  ? ""
                  : `Created on: ${formatDate(fromUnixTime(value.createdOn))}`}
              </Text>
            </div>
          </Flex>
          <Button
            color="primary"
            onClick={() => setModal(true)}
            disabled={forms == null}
          >
            {forms == null ? "Loading" : "Choose"}
          </Button>
        </Flex>
      </Container>
    </>
  );
}
